import fatherStyles from "../settings/Settings.module.scss";
import { Card, CardContent, InputAdornment, Typography, Box, IconButton, TextField, Tooltip, CircularProgress, Switch, FormControlLabel, FormGroup } from "@mui/material";
import { useRef, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import Button from "@mui/material/Button";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useRecoilValue, useRecoilState } from "recoil";
import { greyLighterState, hideTickertapeState, textColorState, tickerTapeState, tickerWSState, userState } from "@/lib/store";
import { findInstruments } from "@/controllers/find-instruments";
import Autocomplete from "@mui/material/Autocomplete";
import ClearIcon from "@mui/icons-material/Clear";
import { getStockInfoOnClient } from "@/controllers/stock";
import { useDebounce } from "react-use";
import { authenticate, saveSettings } from "@/controllers/auth";
import { classNames } from "@/lib/utils";
import styles from "./TickertapeSettings.module.scss";
import SaveIcon from "@mui/icons-material/Save";
import { getTopBarValues } from "@/controllers/instruments";
import SnackBar from "../snack-bar/SnackBar";
interface PropsTypes {
    isModal?: boolean;
}

const TickertapeSettings = ({ isModal }: PropsTypes) => {
    const defaultOptions = [
        { id: 1, name: "DJIA" },
        { id: 2, name: "S&P 500" },
        { id: 3, name: "Nasdaq Comp" },
        { id: 4, name: "Nasdaq 100" },
        { id: 5, name: "OIL (WTI)" },
        { id: 7, name: "ESTOXX50" },
    ];

    const greyLighter = useRecoilValue(greyLighterState);
    const textColor = useRecoilValue(textColorState);

    const [tickerStocks, setTickerStocks] = useRecoilState(tickerTapeState);
    const [subscribedTickers, setSubscribedTickers] = useRecoilState(tickerWSState);
    const [hideTickertape, setHideTickertape] = useRecoilState(hideTickertapeState);

    const [localTickerStocks, setLocalTickerStocks] = useState(tickerStocks);
    const [options, setOptions] = useState<any>(defaultOptions);
    const [open, setOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [newValue, setNewValue] = useState("");
    const [user, setUser] = useRecoilState(userState);
    const index = useRef(0);

    const [snackBar, setSnackBar] = useState<{ open: boolean; text: string; severity: "success" | "error" | "info" | "warning" }>({
        open: false,
        text: "",
        severity: "success",
    });

    const handleSaveSettings = () => {
        if (user.subscription_plan === "ORTEX Visitor") return setSnackBar({ open: true, text: "You need to be a registered user to save your settings", severity: "error" });
        try {
            if (process.env.NEXT_PUBLIC_VERCEL_ENV) {
                saveSettings("tickerTape", "tickertape-settings", localTickerStocks)
                    .then(authenticate)
                    .then((data) => {
                        setUser(data);
                    })
                    .then(() => {
                        setSnackBar({ open: true, text: "Tickertape settings saved", severity: "success" });
                        setTickerStocks(localTickerStocks);
                        setSubscribedTickers([]);
                    });
            } else {
                setTickerStocks(localTickerStocks);
                setSubscribedTickers([]);
                setSnackBar({ open: true, text: "Tickertape settings saved", severity: "success" });
            }
        } catch (error) {
            setSnackBar({ open: true, text: "Error saving settings", severity: "error" });
        }
    };

    const handleVisibilityChange = (stockName: string) => {
        const visibility = localTickerStocks[stockName].visibility;
        const newStocks = {
            ...localTickerStocks,
            [stockName]: {
                ...localTickerStocks[stockName],
                visibility: !visibility,
            },
        };

        setLocalTickerStocks(newStocks);
    };

    const removeTickerStock = (stockName: string) => {
        if (getVisibleTickerStocks() <= 5) {
            setSnackBar({ open: true, text: "You should have more than 5 stocks on the ticker carousel", severity: "error" });
            return;
        } else {
            const newStocks = {
                ...localTickerStocks,
            };

            delete newStocks[stockName];

            setLocalTickerStocks(newStocks);
        }
    };

    const getInstruments = (value: string) => {
        setLoading(true);
        findInstruments(value)
            .then((data) => {
                const newOptions = data
                    .filter((newInstruments) => !options.some((optInstruments) => optInstruments.id === newInstruments.value))
                    .map((instrument) => ({
                        key: instrument.ticker,
                        id: instrument.value,
                        name: instrument.exchange + ":" + instrument.ticker,
                    }));

                setOptions([...newOptions, ...defaultOptions]);
            })
            .then(() => {
                setLoading(false);
            });
    };

    const addTickerStock = (value: string) => {
        if (Object.entries(localTickerStocks).length === 15) {
            setSnackBar({ open: true, text: "You can only have 15 stocks on the ticker tape", severity: "error" });
            return;
        }
        getInstruments(value);
    };

    const handleChange = (event, newValue, reason) => {
        if (event.type === "keydown" && reason === "removeOption") {
            console.warn(reason, newValue);
            return;
        }
        if (newValue !== null && newValue !== undefined && localTickerStocks[newValue] === undefined) {
            if (newValue.key) {
                const stocks = Object.values(localTickerStocks).filter((el) => el.stock_id !== undefined);
                getStockInfoOnClient(newValue.id).then((data) => {
                    if (data.last_close !== undefined) {
                        const newStocks = {
                            ...localTickerStocks,
                            [newValue.name]: {
                                stock_id: data.stock_id,
                                name: data?.name,
                                visibility: true,
                                exchange: data.exchangesymbol,
                                price: data.last_close,
                                lastPrice: data.last_close,
                                lastChange: 0,
                                currency: data.currency,
                                ticker: data.ticker,
                                isin: data.isin,
                                order: stocks.length,
                            },
                        };

                        setLocalTickerStocks(newStocks);
                    }
                });
            } else {
                const localEntries = Object.keys(localTickerStocks);
                if (localEntries.findIndex((el) => el === newValue.name) > 0) {
                    const newStocks = { ...localTickerStocks };
                    delete newStocks[newValue.name];
                    setLocalTickerStocks(newStocks);
                } else {
                    setNewValue(newValue.name);

                    getTopBarValues().then((res) => {
                        const newStocks = {
                            ...localTickerStocks,
                            [newValue.code]: {
                                name: newValue.name,
                                price: res[newValue.code].price,
                                lastPrice: res[newValue.code].price,
                                lastChange: res[newValue.code].price,
                                timestamp: res[newValue.code].ts,
                                visibility: true,
                            },
                        };
                        setLocalTickerStocks(newStocks);
                    });
                }
            }
        }
        index.current++;
    };

    useDebounce(
        () => {
            if (newValue !== "") {
                addTickerStock(newValue);
            } else {
                setOptions(defaultOptions);
            }
        },
        300,
        [newValue]
    );
    const getVisibleTickerStocks = () => {
        let count = 0;
        for (const [key, value] of Object.entries(localTickerStocks)) {
            if (value.visibility && !value.removed) {
                count++;
            }
        }
        return count;
    };

    const handleTickertapeVisibilityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setHideTickertape(event.target.checked);
        const newVisibility = { isHidden: event.target.checked };

        // When showing the tickertape, reset subscribed tickers so components re-subscribe to WS
        if (!event.target.checked) {
            setSubscribedTickers([]);
        }

        if (process.env.NEXT_PUBLIC_VERCEL_ENV) saveSettings("tickerTape", "tickertape-visibility", newVisibility);
    };

    return (
        // Card wrapper deliberately stripped of its default MUI Paper
        // background — the parent SettingsSection on /settings already
        // provides a tinted surface and the Card's own dark `paper`
        // colour was reading as a "different box inside the box".
        // `background: transparent` + `boxShadow: none` makes the
        // Card invisible as a surface; the children just live inside
        // the SettingsSection. `height: 100%` lets the column grow to
        // match the height of its grid-row sibling, and the
        // flex-column layout below splits the body into
        //   (a) header (Hide toggle + search), fixed-height,
        //   (b) stocks list, flex:1 + overflow: auto so longer lists
        //       scroll inside the card,
        //   (c) Save button, fixed-height, always visible at the
        //       bottom.
        // Without this the list could push the Save button out of the
        // grid track entirely on long lists.
        <Card
            className={classNames(fatherStyles.settingsCard, styles.settingsContainer)}
            sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                background: "transparent",
                boxShadow: "none",
                height: "100%",
                minHeight: 0,
            }}
            elevation={0}
        >
            <Box sx={{ flexShrink: 0 }}>
                <div className={classNames(fatherStyles["settings-item"], isModal ? styles["settings-item"] : "")}>
                    <FormGroup sx={{ width: "fit-content" }}>
                        <FormControlLabel sx={{ width: "fit-content" }} control={<Switch checked={hideTickertape} onChange={handleTickertapeVisibilityChange} />} label={"Hide Tickertape"} labelPlacement="start" />
                    </FormGroup>
                </div>
            </Box>
            {!hideTickertape && (
                <CardContent
                    sx={{
                        // CardContent becomes a flex column too so its
                        // children (search field, scrollable list, save
                        // button) can use flex sizing rather than the
                        // brittle percentage heights the previous
                        // version relied on.
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                        px: isModal && 0,
                        background: "transparent",
                    }}
                >
                    <Typography variant="body1" sx={{ flexShrink: 0 }}>
                        Add new stocks to ticker
                    </Typography>
                    <Autocomplete
                        open={open}
                        size={isModal ? "small" : "medium"}
                        onChange={handleChange}
                        clearOnBlur
                        blurOnSelect
                        value={null}
                        onClose={() => setOpen(false)}
                        noOptionsText="No labels"
                        loading={loading}
                        renderTags={() => null}
                        filterOptions={(x) => x}
                        renderOption={(props, option, { selected }) => (
                            <li {...props} key={option.name + option.id}>
                                <Box
                                    sx={{
                                        flexGrow: 1,
                                    }}
                                >
                                    {option.name}
                                </Box>
                            </li>
                        )}
                        isOptionEqualToValue={(option, value) => option.name === value.name || true}
                        getOptionLabel={(option) => option.name}
                        options={options}
                        disableClearable={true}
                        sx={{
                            padding: 0,
                            "& .MuiSvgIcon-root": {
                                color: textColor,
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                ref={params.InputProps.ref}
                                onChange={(event) => setNewValue(event.target.value)}
                                onFocus={() => setOpen(true)}
                                onBlur={() => {
                                    setOpen(false);
                                    setNewValue("");
                                }}
                                inputProps={params.inputProps}
                                placeholder="Search a Stock"
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon htmlColor={greyLighter} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: loading && (
                                        <InputAdornment position="end">
                                            <CircularProgress color="inherit" size={20} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                        clearIcon={<ClearIcon fontSize="small" sx={{ color: "red" }} />}
                    />
                    {/* Scrollable stock list. `flex: 1` claims the
                        remaining space between the Autocomplete and
                        the Save button; `minHeight: 0` is the standard
                        flexbox trick that lets a child with overflow
                        actually scroll inside its flex parent (without
                        it the list pushes the parent taller and the
                        Save button gets shoved out of view). */}
                    <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5, p: isModal && 1 }}>
                        {Object.entries(localTickerStocks).length > 0 &&
                            Object.entries(localTickerStocks).map((stock, index) => {
                                if (stock[0] === "undefined" || stock[1]?.removed || stock[1]?.name === undefined) {
                                    return;
                                } else {
                                    return (
                                        <Box key={stock[1].name} sx={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                            <Typography variant="body1" sx={{ width: "60%" }}>
                                                {stock[1]?.name}
                                            </Typography>
                                            <Box className={isModal ? styles.actionBtns : ""}>
                                                <Tooltip arrow title="Toggle visibility">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={() => handleVisibilityChange(stock[0])}
                                                        sx={{
                                                            color: textColor,
                                                        }}
                                                        disabled={stock[1]?.visibility && getVisibleTickerStocks() <= 5}
                                                    >
                                                        {stock[1]?.visibility ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip arrow title="Delete">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={() => removeTickerStock(stock[0])}
                                                        onMouseDown={(e) => e.preventDefault()}
                                                        sx={{
                                                            color: "red",
                                                        }}
                                                        disabled={getVisibleTickerStocks() <= 5}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    );
                                }
                            })}
                    </Box>
                    {/* Save button pinned at the bottom of the card.
                        `flexShrink: 0` keeps it visible regardless of
                        how long the stock list above it grows. */}
                    <Tooltip title={user.subscription_plan === "ORTEX Visitor" ? "You need to be a registered user to save your settings" : ""} arrow>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, pt: 1 }}>
                            <Button onClick={handleSaveSettings} variant="contained" sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-around", color: "#fff" }} disabled={user.subscription_plan === "ORTEX Visitor"}>
                                Save settings
                                <SaveIcon sx={{ marginLeft: 0.5 }} />
                            </Button>
                        </Box>
                    </Tooltip>
                </CardContent>
            )}
            <SnackBar open={snackBar.open} setOpen={(open) => setSnackBar({ ...snackBar, open })} severity={snackBar.severity} text={snackBar.text} />
        </Card>
    );
};

export default TickertapeSettings;
